/**
 * Bänder Feature — RTK Query endpoints (mutations).
 *
 * Create, update Bänder and add field definitions.
 * Uses queryFn pattern matching settingsApi.ts storage mutations.
 *
 * @module baende/api/mutations
 */

import { rootApi } from "../../../lib/api/rootApi";
import { supabase } from "@flexwise/shared/supabase";
import type {
  CreateBandInput,
  UpdateBandInput,
  BandFieldInput,
  BandCreateResponse,
  BandUpdateResponse,
  BandAddFieldsResponse,
  SaveBandLogEntryInput,
} from "../types/baendeTypes";

export const baendeMutationApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    // ── Create a new Band with fields ────────────────────────────────
    createBand: builder.mutation<BandCreateResponse, CreateBandInput>({
      query: (input) => ({
        type: "rpc",
        rpc: "fn_baende_create",
        params: {
          p_name: input.name,
          p_description: input.description ?? null,
          p_icon: input.icon ?? "book-open",
          p_color: input.color ?? "#10b981",
          p_fields: input.fields,
        },
      }),
      invalidatesTags: ["Baende" as never],
    }),

    // ── Update Band metadata ─────────────────────────────────────────
    updateBand: builder.mutation<BandUpdateResponse, UpdateBandInput>({
      query: (input) => ({
        type: "rpc",
        rpc: "fn_baende_update",
        params: {
          p_band_id: input.band_id,
          p_name: input.name ?? null,
          p_description: input.description ?? null,
          p_icon: input.icon ?? null,
          p_color: input.color ?? null,
          p_is_active: input.is_active ?? null,
          p_analytics_config: input.analytics_config ?? null,
        },
      }),
      invalidatesTags: ["Baende" as never],
    }),

    // ── Add fields to an existing Band ───────────────────────────────
    addBandFields: builder.mutation<
      BandAddFieldsResponse,
      { band_id: string; fields: BandFieldInput[] }
    >({
      query: ({ band_id, fields }) => ({
        type: "rpc",
        rpc: "fn_baende_add_fields",
        params: {
          p_band_id: band_id,
          p_fields: fields,
        },
      }),
      invalidatesTags: ["Baende" as never],
    }),

    // ── Replace all fields for a Band ─────────────────────────────────
    replaceBandFields: builder.mutation<
      { action: string; band_id: string; field_count: number },
      { band_id: string; fields: BandFieldInput[] }
    >({
      async queryFn({ band_id, fields }) {
        try {
          const validFields = fields.filter((f) => f.field_name.trim() !== "");

          // Preferred path: use the dedicated RPC when deployed.
          const { data, error } = await supabase.rpc("fn_baende_replace_fields", {
            p_band_id: band_id,
            p_fields: validFields,
          });

          if (!error) {
            return {
              data:
                (data as { action: string; band_id: string; field_count: number }) ??
                {
                  action: "fields_replaced",
                  band_id,
                  field_count: validFields.length,
                },
            };
          }

          // Backward-compat fallback for environments where the RPC is not yet deployed.
          const isMissingRpc =
            (error as { code?: string }).code === "PGRST202" ||
            error.message.includes("fn_baende_replace_fields");

          if (!isMissingRpc) {
            return {
              error: {
                status: "CUSTOM_ERROR" as const,
                data: error.message,
                error: error.message,
              },
            };
          }

          const { error: deleteError } = await supabase
            .from("baende_fields")
            .delete()
            .eq("band_id", band_id);

          if (deleteError) {
            return {
              error: {
                status: "CUSTOM_ERROR" as const,
                data: deleteError.message,
                error: deleteError.message,
              },
            };
          }

          if (validFields.length > 0) {
            const rows = validFields.map((field, index) => ({
              band_id,
              field_name: field.field_name.trim(),
              field_type: field.field_type,
              options: field.field_type === "dropdown" ? (field.options ?? []) : null,
              is_required: !!field.is_required,
              display_order: index,
            }));

            const { error: insertError } = await supabase
              .from("baende_fields")
              .insert(rows);

            if (insertError) {
              return {
                error: {
                  status: "CUSTOM_ERROR" as const,
                  data: insertError.message,
                  error: insertError.message,
                },
              };
            }
          }

          const { error: touchError } = await supabase
            .from("baende")
            .update({ updated_at: new Date().toISOString() })
            .eq("id", band_id);

          if (touchError) {
            return {
              error: {
                status: "CUSTOM_ERROR" as const,
                data: touchError.message,
                error: touchError.message,
              },
            };
          }

          return {
            data: {
              action: "fields_replaced",
              band_id,
              field_count: validFields.length,
            },
          };
        } catch (err: unknown) {
          return {
            error: {
              status: "CUSTOM_ERROR" as const,
              data: String(err),
              error: String(err),
            },
          };
        }
      },
      invalidatesTags: ["Baende" as never],
    }),

    // ── Save a specific daily log entry for a Band ───────────────────
    saveBandLogEntry: builder.mutation<
      { success: boolean; log_id?: string; action?: "reset" },
      SaveBandLogEntryInput
    >({
      query: (input) => ({
        type: "rpc",
        rpc: "fn_baende_log_entry",
        params: {
          p_assignment_id: input.assignment_id,
          p_lesson_id: input.lesson_id,
          p_class_id: input.class_id,
          p_field_values: input.field_values,
          p_status: input.status ?? "completed",
          p_moved_to_period_id: input.moved_to_period_id ?? null,
        },
      }),
      // The Klassenbuch diary + live timetable read band logs out of the
      // Stundenplan week bundle (fn_stundenplan_get_week_bundle), so we have
      // to invalidate that tag too — otherwise a Verschoben save only shows
      // up after a full page reload.
      invalidatesTags: [
        "BaendeLogs" as never,
        { type: "Stundenplan", id: "WEEK" } as never,
      ],
    }),
  }),
});

export const {
  useCreateBandMutation,
  useUpdateBandMutation,
  useAddBandFieldsMutation,
  useReplaceBandFieldsMutation,
  useSaveBandLogEntryMutation,
} = baendeMutationApi;
